<template>
  <div class="bdx-account-name position-relative">
    <BaseInput
      :value="value"
      name="Account Name"
      :size="size"
      :rules="required ? 'required' : undefined"
      :placeholder="placeholder"
      :disabled="disabled"
      autocomplete="off"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
    >
      <template v-if="showLabel" v-slot:label>
        Account Name
        <span v-if="required" class="text-danger">*</span>
      </template>
    </BaseInput>

    <ul v-if="showSuggestions && !disabled" class="bdx-account-name__list">
      <li v-if="isFetching" class="bdx-account-name__hint">Loading Account Names…</li>
      <template v-else>
        <li
          v-for="option in filteredOptions"
          :key="option.account_name"
          @mousedown.prevent="selectOption(option.account_name)"
        >
          {{ option.account_name }}
          <small v-if="!option.sources.includes('CASHBOOK')" class="text-muted">(not in cash book)</small>
        </li>
        <li
          v-if="showNewOption"
          class="bdx-account-name__new"
          @mousedown.prevent="selectOption(typedValue)"
        >
          + New: "{{ typedValue }}"
        </li>
        <li v-if="!filteredOptions.length && !showNewOption" class="bdx-account-name__hint">
          {{ umr ? 'No cash book Account Names for this UMR - type to add a new one' : 'Type to add an Account Name' }}
        </li>
      </template>
    </ul>
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch } from '@vue/composition-api'
import { LincolnGlobalUMRService } from '@services'
import { BaseInput } from '@components/base'

// Mirrors dbo.fn_FCP_Norm / Lincoln _fcp_norm, so "Acme Ltd" and "ACME LTD " count as the same name.
const normalise = (value) =>
  String(value || '')
    .replace(/[ \t\r\n]/g, ' ')
    .trim()
    .toUpperCase()

// Shared across every instance, so reopening the edit form or moving between rows of the
// same UMR doesn't refetch. Cleared per UMR after a save so a new name shows up next time.
const optionsCache = new Map()

export const clearAccountNameCache = (umr) => {
  if (umr) optionsCache.delete(umr)
  else optionsCache.clear()
}

const fetchAccountNames = (umr) => {
  if (!optionsCache.has(umr)) {
    const request = LincolnGlobalUMRService.accountNames(umr)
      .then(({ data }) => (data && Array.isArray(data.data) ? data.data : []))
      .catch(() => {
        // Don't cache failures; free text still works, so the form isn't blocked.
        optionsCache.delete(umr)
        return []
      })
    optionsCache.set(umr, request)
  }
  return optionsCache.get(umr)
}

// LIQ-2410: Account Name type-ahead scoped by the key's UMR. Options come from cash books
// ingested for that UMR plus names already saved on keys for it; anything else can be typed
// in as a new name. No validation beyond that - a blank or unmatched name is allowed.
export default defineComponent({
  name: 'BDXEditFormAccountName',
  components: {
    BaseInput
  },
  props: {
    value: String,
    umr: String,
    size: {
      type: String,
      default: 'sm'
    },
    required: Boolean,
    disabled: Boolean,
    placeholder: {
      type: String,
      default: 'Select or type an Account Name'
    },
    showLabel: {
      type: Boolean,
      default: true
    }
  },
  emits: ['input'],
  setup(props, { emit }) {
    const showSuggestions = ref(false)
    const isFetching = ref(false)
    const options = ref([])

    const loadOptions = async () => {
      if (!props.umr) {
        options.value = []
        return
      }
      const umr = props.umr
      isFetching.value = true
      const result = await fetchAccountNames(umr)
      // Ignore a stale response if the UMR changed while it was in flight.
      if (umr === props.umr) {
        options.value = result
        isFetching.value = false
      }
    }

    watch(() => props.umr, () => {
      options.value = []
    })

    const typedValue = computed(() => String(props.value || '').trim())

    const filteredOptions = computed(() => {
      const query = typedValue.value.toLowerCase()
      if (!query) return options.value
      return options.value.filter((option) => option.account_name.toLowerCase().includes(query))
    })

    const showNewOption = computed(() => {
      const typed = normalise(typedValue.value)
      if (!typed) return false
      return !options.value.some((option) => normalise(option.account_name) === typed)
    })

    const onInput = (newValue) => {
      emit('input', newValue)
      showSuggestions.value = true
    }

    const onFocus = () => {
      showSuggestions.value = true
      if (!options.value.length) loadOptions()
    }

    const onBlur = () => {
      showSuggestions.value = false
    }

    const selectOption = (accountName) => {
      emit('input', accountName)
      showSuggestions.value = false
    }

    return {
      showSuggestions,
      isFetching,
      typedValue,
      filteredOptions,
      showNewOption,
      onInput,
      onFocus,
      onBlur,
      selectOption
    }
  }
})
</script>

<style scoped>
/* Same look as CashCallAutocompleteInput's list. */
.bdx-account-name__list {
  position: absolute;
  z-index: 1080;
  top: 100%;
  left: 0;
  right: auto;
  min-width: 100%;
  width: max-content;
  max-width: 360px;
  margin: 2px 0 0;
  padding: 4px 0;
  list-style: none;
  background-color: #fff;
  border: 1px solid #e3e8ec;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  max-height: 200px;
  overflow-x: hidden;
  overflow-y: auto;
}

.bdx-account-name__list li {
  padding: 6px 12px;
  font-size: 0.875rem;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bdx-account-name__list li:hover {
  background-color: #f1f3f5;
  color: #1c4a50;
}

.bdx-account-name__list li.bdx-account-name__new {
  border-top: 1px solid #e3e8ec;
  font-weight: 500;
}

.bdx-account-name__list li.bdx-account-name__hint {
  cursor: default;
  color: #6c757d;
  white-space: normal;
}

.bdx-account-name__list li.bdx-account-name__hint:hover {
  background-color: transparent;
}
</style>
